import { nanoid } from "nanoid";
import { useRef, useState, type RefObject } from "react";

export const useDragAndDrop = (opts?: {
  disabled?: boolean;
  multiple?: boolean;
  ref?: RefObject<HTMLElement>;
  value?: File[];
  onChange?: (files?: File[]) => void;
}) => {
  const { disabled, multiple, ref, value, onChange } = opts ?? {};
  const targetRef = useRef<HTMLElement>(ref?.current ?? null);
  const [dragState, setDragState] = useState("");

  const onDragEnter = (event: React.DragEvent) => {
    if (dragState !== "enter") {
      setDragState("enter");
    }

    event.preventDefault();
    event.stopPropagation();
  };

  const onDragOver = (event: React.DragEvent) => {
    if (dragState !== "enter") {
      setDragState("enter");
    }

    event.preventDefault();
    event.stopPropagation();
  };

  const onDragLeave = (event: React.DragEvent) => {
    if (
      dragState !== "" &&
      targetRef?.current?.isEqualNode(event.target as Node)
    ) {
      setDragState("");
    }

    event.preventDefault();
    event.stopPropagation();
  };

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setDragState("");

    if (!disabled) {
      let files: File[] =
        event.dataTransfer.files instanceof FileList
          ? Array.from(event.dataTransfer.files)
          : event.dataTransfer.files;

      if (!multiple && files.length > 1) {
        files = Array.from(files).slice(0, 1);
      }

      if (onChange) {
        onChange(multiple ? [...(value ?? []), ...files] : files);
      }
    }
  };

  return {
    ref: targetRef,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    isDraggingOver: dragState === "enter" && !disabled,
  };
};

export class FileWithId extends File {
  readonly id: string = nanoid(6);

  constructor(bits: BlobPart[], name: string, opts?: FilePropertyBag) {
    super(bits, name, opts);
  }
}
